'use client'

import React, { Suspense, useEffect, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import {
  AuthTextLink,
  MemberAuthShell,
} from '@/components/member/MemberAuthShell'
import { readCommunityCookie } from '@/lib/tenant/client'
import { getAccessToken } from '@/lib/admin-proxy'
import { toast } from 'sonner'

type Step = 'email' | 'sent' | 'session' | 'neutral'

interface CommunityBrand {
  id: string | null
  name: string | null
  slug: string | null
}

function LoginForm() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const supabase = createClient()

  const [community, setCommunity] = useState<CommunityBrand>({
    id: null,
    name: null,
    slug: null,
  })
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '')
  const [step, setStep] = useState<Step>('email')
  const [loading, setLoading] = useState(false)
  const [booting, setBooting] = useState(true)
  const [sessionEmail, setSessionEmail] = useState<string | null>(null)
  const [sessionName, setSessionName] = useState<string | null>(null)

  useEffect(() => {
    const cookie = readCommunityCookie()
    setCommunity({
      id: cookie.id,
      name: cookie.name,
      slug: cookie.slug,
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    async function boot() {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (cancelled) return

      if (!session?.user) {
        setBooting(false)
        return
      }

      const token = session.access_token
      const res = await fetch('/api/auth/community-login', {
        headers: { authorization: `Bearer ${token}` },
      })
      const json = await res.json().catch(() => ({ status: 'none' }))
      if (cancelled) return

      const status = json.status as string
      if (status === 'active') {
        setSessionEmail(session.user.email ?? json.email ?? null)
        setSessionName(
          (session.user.user_metadata?.full_name as string | undefined) ?? null,
        )
        setStep('session')
        setBooting(false)
        return
      }
      if (status === 'suspended') {
        router.replace('/membership-suspended')
        return
      }
      setStep('neutral')
      setBooting(false)
    }
    void boot()
    return () => {
      cancelled = true
    }
  }, [supabase, router])

  useEffect(() => {
    const err = searchParams.get('error')
    if (err === 'auth_callback')
      toast.error('That sign-in link expired. Request a new one below.')
  }, [searchParams])

  async function routeAfterAuth() {
    const token = await getAccessToken()
    const membership = await fetch('/api/auth/community-login', {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.json())
      .catch(() => ({ status: 'none' }))

    if (membership.status === 'suspended') {
      window.location.assign('/membership-suspended')
      return
    }
    if (membership.status !== 'active') {
      setStep('neutral')
      return
    }

    window.location.assign('/member-home')
  }

  async function handleRequestMagicLink(event?: React.FormEvent) {
    event?.preventDefault()
    const normalized = email.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      toast.error('Enter a valid email address')
      return
    }
    if (!community.id) {
      toast.error(
        'Open this page on your community host (e.g. thc.orbyt.club) to get a link.',
      )
      return
    }
    setLoading(true)
    // Advance immediately — gate always returns ok; SMTP must not freeze the UI.
    setStep('sent')
    try {
      await fetch('/api/auth/community-login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: normalized, action: 'request' }),
        signal: AbortSignal.timeout(20_000),
      })
    } catch {
      // Still leave them on "sent" — same public shape either way.
    } finally {
      setLoading(false)
    }
  }

  async function handleContinueAsSession() {
    setLoading(true)
    await routeAfterAuth()
    setLoading(false)
  }

  async function handleNotYou() {
    setLoading(true)
    await supabase.auth.signOut()
    setSessionEmail(null)
    setSessionName(null)
    setStep('email')
    setLoading(false)
  }

  const displayName = community.name ?? 'this community'
  const brand = { name: community.name }

  if (booting) {
    return (
      <MemberAuthShell community={brand} eyebrow="Member sign-in">
        <p className="text-[15px] leading-relaxed text-fg-muted">
          Opening {displayName}…
        </p>
      </MemberAuthShell>
    )
  }

  if (step === 'neutral') {
    return (
      <MemberAuthShell community={brand} eyebrow="Member sign-in">
        <p className="member-meta">invitation required</p>
        <h1 className="member-heading-page mt-3 text-left">
          Check your invitation
        </h1>
        <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-fg-muted">
          {displayName} is invite-only. Open the link from your email, or ask an
          admin to send one to the address on your membership.
        </p>
        <AuthTextLink onClick={() => void handleNotYou()}>
          Use a different email
        </AuthTextLink>
      </MemberAuthShell>
    )
  }

  if (step === 'session' && sessionEmail) {
    return (
      <MemberAuthShell community={brand} eyebrow="Member sign-in">
        <p className="member-meta">welcome back</p>
        <h1 className="member-heading-page mt-3 text-left">
          Continue where you left off
        </h1>
        <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-fg-muted">
          Signed in as{' '}
          <span className="text-fg">
            {sessionName ? `${sessionName} · ${sessionEmail}` : sessionEmail}
          </span>
        </p>
        <button
          type="button"
          disabled={loading}
          onClick={() => void handleContinueAsSession()}
          className="btn-primary mt-10 w-full tracking-tight disabled:opacity-60"
        >
          {loading ? 'Continuing…' : `Continue to ${displayName}`}
        </button>
        <AuthTextLink onClick={() => void handleNotYou()}>
          Use a different email
        </AuthTextLink>
      </MemberAuthShell>
    )
  }

  if (step === 'sent') {
    return (
      <MemberAuthShell community={brand} eyebrow="Member sign-in">
        <p className="member-meta">check your inbox</p>
        <h1 className="member-heading-page mt-3 text-left">Check your email</h1>
        <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-fg-muted">
          If <span className="text-fg">{email.trim().toLowerCase()}</span> belongs
          in {displayName}, we sent a sign-in link. Open it on this device — it
          expires in about an hour.
        </p>
        <button
          type="button"
          disabled={loading}
          onClick={() => void handleRequestMagicLink()}
          className="btn-primary mt-10 w-full tracking-tight disabled:opacity-60"
        >
          {loading ? 'Sending…' : 'Resend link'}
        </button>
        <AuthTextLink
          onClick={() => {
            setStep('email')
            setLoading(false)
          }}
        >
          Use a different email
        </AuthTextLink>
      </MemberAuthShell>
    )
  }

  return (
    <MemberAuthShell community={brand} eyebrow="Member sign-in">
      <p className="member-meta">sign in</p>
      <h1 className="member-heading-page mt-3 text-left">
        Sign in to {displayName}
      </h1>
      <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-fg-muted">
        Enter the email on your membership. We&apos;ll send a one-tap link — no
        password needed.
      </p>

      <form
        className="mt-10 flex w-full flex-col gap-5"
        onSubmit={(e) => void handleRequestMagicLink(e)}
      >
        <label htmlFor="email" className="block text-left">
          <span className="text-[11px] font-medium uppercase tracking-[0.04em] text-fg-muted">
            Email
          </span>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="member-compose-input mt-2 !min-h-0 py-3 text-left"
          />
        </label>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary mt-2 w-full tracking-tight disabled:opacity-60"
        >
          {loading ? 'Sending…' : 'Email me a sign-in link'}
        </button>
      </form>
    </MemberAuthShell>
  )
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <MemberAuthShell eyebrow="Member sign-in">
          <p className="text-[15px] text-fg-muted">Opening…</p>
        </MemberAuthShell>
      }
    >
      <LoginForm />
    </Suspense>
  )
}
